import React, { useEffect, useState } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RefreshCw, TrendingUp, Users, Activity, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function GrowthPage() {
  const [analytics, setAnalytics] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [analyticsRes, trendRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/growth?period=${period}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/daily-trend?days=7`, { credentials: 'include' })
      ]);
      
      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
      }
      
      if (trendRes.ok) {
        const data = await trendRes.json();
        setTrend(data);
      }
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const StatCard = ({ title, value, icon: Icon, subtitle, color = "text-primary" }) => (
    <GroupBox label={title} className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <Icon className={`h-8 w-8 ${color} opacity-50`} />
      </div>
    </GroupBox>
  );

  const maxTrendValue = Math.max(...trend.map(t => t.total), 1);

  return (
    <div className="space-y-4" data-testid="growth-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-bold">Growth Tracker</h1>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {analytics && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-3">
            <StatCard 
              title="Total Clients" 
              value={analytics.clients.total}
              icon={Users}
              subtitle={`+${analytics.clients.new} new`}
              color="text-blue-500"
            />
            <StatCard 
              title="Transactions" 
              value={analytics.transactions.total_count}
              icon={Activity}
              subtitle={`Match: ${analytics.transactions.match_count} | Session: ${analytics.transactions.session_count}`}
              color="text-emerald-500"
            />
            <StatCard 
              title="Total Volume" 
              value={`₹${analytics.volume.total.toLocaleString()}`}
              icon={DollarSign}
              subtitle={`Match: ₹${analytics.volume.match.toLocaleString()}`}
              color="text-amber-500"
            />
            <StatCard 
              title="Activities" 
              value={analytics.activities.matches_created + analytics.activities.sessions_created}
              icon={TrendingUp}
              subtitle={`Matches: ${analytics.activities.matches_created} | Sessions: ${analytics.activities.sessions_created}`}
              color="text-purple-500"
            />
          </div>

          {/* Daily Trend Chart */}
          <GroupBox label="7-Day Transaction Trend" className="p-4">
            <div className="flex items-end justify-between h-40 gap-2">
              {trend.map((day, index) => (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-emerald-500 rounded-t transition-all"
                      style={{ height: `${(day.match_txns / maxTrendValue) * 100}px` }}
                      title={`Match: ${day.match_txns}`}
                    />
                    <div 
                      className="w-full bg-blue-500 rounded-t transition-all"
                      style={{ height: `${(day.session_txns / maxTrendValue) * 100}px` }}
                      title={`Session: ${day.session_txns}`}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                  <span className="text-xs font-medium">{day.total}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-emerald-500 rounded" />
                <span className="text-xs">Match Txns</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded" />
                <span className="text-xs">Session Txns</span>
              </div>
            </div>
          </GroupBox>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-3">
            <GroupBox label="Match Summary" className="p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Matches Created</span>
                  <span className="font-medium">{analytics.activities.matches_created}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Transactions</span>
                  <span className="font-medium">{analytics.transactions.match_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Volume</span>
                  <span className="font-medium">₹{analytics.volume.match.toLocaleString()}</span>
                </div>
              </div>
            </GroupBox>
            
            <GroupBox label="Session Summary" className="p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Sessions Created</span>
                  <span className="font-medium">{analytics.activities.sessions_created}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Transactions</span>
                  <span className="font-medium">{analytics.transactions.session_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Volume</span>
                  <span className="font-medium">₹{analytics.volume.session.toLocaleString()}</span>
                </div>
              </div>
            </GroupBox>
          </div>
        </>
      )}
    </div>
  );
}
